// web/src/pages/TermsAck.tsx
import { useEffect, useMemo, useState } from "react";
import type { JSX } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import {
  onAuthStateChanged,
  deleteUser,
  signOut,
  type User as FbUser,
} from "firebase/auth";
import { auth, db } from "@/lib/firebase";
import {
  doc,
  getDoc,
  setDoc,
  serverTimestamp,
  deleteDoc,
} from "firebase/firestore";

import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/use-toast";

/** Bump this when you publish new legal terms so returning users re-ack */
const TERMS_VERSION = "2025-08-21";

/** Age bands we explicitly gate for */
type AgeBand = "u13" | "13-17" | "18+";

/** Local UI state for required acknowledgements */
type AcksState = {
  terms: boolean;
  privacy: boolean;
  community: boolean;
  data: boolean;
  guardianConsent: boolean; // only relevant for 13–17
  marketingOptIn: boolean; // optional
};

const defaultAcks: AcksState = {
  terms: false,
  privacy: false,
  community: false,
  data: false,
  guardianConsent: false,
  marketingOptIn: false,
};

type UserDoc = {
  username?: string | null;
  legal?: {
    version?: string | null;
    acceptedAt?: unknown;
    accepted?: boolean;
    ageConfirmed?: boolean;
    age?: number | null;
    ageBand?: AgeBand | null;
    guardianConsent?: boolean | null;
  } | null;
};

/** Pixel-art parchment scroll container (pure CSS, no images). */
function ScrollPaper({
  children,
  className = "",
  title,
  subtitle,
}: {
  children: React.ReactNode;
  className?: string;
  title?: string;
  subtitle?: string;
}) {
  return (
    <section className="mx-auto w-full max-w-3xl">
      {/* Header above the scroll to match site vibe */}
      {(title || subtitle) && (
        <header className="text-center mb-4">
          {title && (
            <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-white">
              {title}
            </h1>
          )}
          {subtitle && (
            <p className="text-white/80 mt-1 max-w-prose mx-auto">{subtitle}</p>
          )}
        </header>
      )}

      {/* The scroll */}
      <div
        className={[
          "relative mx-auto",
          // Pixel frame & glow that harmonizes with site
          "rounded-[10px] border-4 border-[#442a12] shadow-[0_0_0_2px_#a7783a_inset,0_0_35px_rgba(255,215,160,0.15)]",
          // Background parchment with light speckle using layered gradients
          "bg-[linear-gradient(0deg,rgba(255,240,200,0.85),rgba(245,225,185,0.9)),repeating-linear-gradient(45deg,rgba(0,0,0,0.06)_0px,rgba(0,0,0,0.06)_2px,transparent_2px,transparent_6px)]",
          // Pixel-ish edges
          "outline outline-2 outline-[#1a102a]/60",
          className,
        ].join(" ")}
        // Faux rolled ends using ::before/::after via Tailwind arbitrary styles
      >
        {/* Top roll */}
        <div
          aria-hidden
          className="absolute -top-4 left-1/2 -translate-x-1/2 h-4 w-[86%] rounded-t-[999px] bg-[radial-gradient(ellipse_at_center,rgba(80,50,20,0.75),rgba(40,24,10,0.9))] shadow-[0_6px_0_0_rgba(0,0,0,0.35)]"
          style={{ imageRendering: "pixelated" as any }}
        />
        {/* Bottom roll */}
        <div
          aria-hidden
          className="absolute -bottom-4 left-1/2 -translate-x-1/2 h-4 w-[86%] rounded-b-[999px] bg-[radial-gradient(ellipse_at_center,rgba(80,50,20,0.75),rgba(40,24,10,0.9))] shadow-[0_-6px_0_0_rgba(0,0,0,0.35)]"
          style={{ imageRendering: "pixelated" as any }}
        />

        <div
          className={[
            "relative z-[1]",
            "px-6 py-7 sm:px-10 sm:py-10",
            "text-[#20110a]",
          ].join(" ")}
          style={{ imageRendering: "pixelated" as any }}
        >
          {/* Decorative “title ribbon” inside scroll */}
          <div className="mx-auto -mt-1 mb-6 w-fit px-4 py-1 rounded border-2 border-[#653c17] bg-[#a96e2a] text-center shadow-[inset_0_-2px_0_#7c4d1e,0_2px_0_#2a1a0b]">
            <span className="text-sm sm:text-base font-extrabold tracking-wider uppercase text-white">
              Acknowledgement Charter
            </span>
          </div>

          {children}
        </div>
      </div>
    </section>
  );
}

export default function TermsAck(): JSX.Element {
  const navigate = useNavigate();
  const [params] = useSearchParams();

  // Where to go after accepting (defaults to Settings)
  const next = params.get("next") || "/settings";
  // Where to send the user if they decline (defaults to landing)
  const ret = params.get("return") || "/";

  const [fbUser, setFbUser] = useState<FbUser | null>(auth.currentUser);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);

  const [age, setAge] = useState<AgeBand | null>(null);
  const [acks, setAcks] = useState<AcksState>(defaultAcks);

  // Subscribe to auth and pre-check whether the user already accepted this version
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      setFbUser(u);
      if (!u) {
        setLoading(false);
        return;
      }
      try {
        const ref = doc(db, "users", u.uid);
        const snap = await getDoc(ref);
        const data: UserDoc = snap.exists() ? (snap.data() as UserDoc) : {};
        if (data?.legal?.version === TERMS_VERSION) {
          navigate(next, { replace: true });
          return;
        }
      } catch {
        // ignore; allow user to continue
      } finally {
        setLoading(false);
      }
    });
    return () => unsub();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const canAccept: boolean = useMemo(() => {
    if (age === "u13") return false; // hard stop
    const baseChecked =
      acks.terms && acks.privacy && acks.community && acks.data;
    if (age === "13-17") return baseChecked && acks.guardianConsent;
    if (age === "18+") return baseChecked;
    return false;
  }, [age, acks]);

  async function handleAccept(): Promise<void> {
    if (!fbUser || !canAccept) return;

    setSaving(true);
    try {
      // Set the canonical "legal.*" fields the gate expects
      await setDoc(
        doc(db, "users", fbUser.uid),
        {
          legal: {
            version: TERMS_VERSION,
            acceptedAt: serverTimestamp(),
            accepted: true,
            ageConfirmed: true,
            ageBand: age,
            guardianConsent:
              age === "13-17" ? Boolean(acks.guardianConsent) : null,
          },
          email_prefs: {
            marketing: Boolean(acks.marketingOptIn),
          },
          updatedAt: serverTimestamp(),
        },
        { merge: true },
      );

      toast({ title: "Thanks!", description: "You’re all set." });
      navigate("/onboarding", { replace: true });
    } catch {
      toast({
        title: "Could not save your acknowledgement",
        description: "Please try again in a moment.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  }

  async function handleDecline(): Promise<void> {
    const u = auth.currentUser;
    if (!u) {
      window.location.assign(ret);
      return;
    }

    try {
      // Read username so we can free the reservation
      const uref = doc(db, "users", u.uid);
      const usnap = await getDoc(uref);
      const data: UserDoc = usnap.exists() ? (usnap.data() as UserDoc) : {};
      const slug = (data.username ?? "").toString();

      // Best-effort cleanup: delete mapping + user doc
      await Promise.allSettled([
        slug ? deleteDoc(doc(db, "usernames", slug)) : Promise.resolve(),
        deleteDoc(uref),
      ]);

      try {
        await deleteUser(u);
      } catch {
        // If recent login required or blocked, sign out instead
        await signOut(auth);
      }
    } finally {
      window.location.assign(ret);
    }
  }

  if (loading) {
    return (
      <main className="min-h-[calc(100vh-64px)] bg-gradient-to-b from-[#0b0620] via-[#0a0420] to-[#02010f] px-4 py-10">
        <section className="mx-auto w-full max-w-2xl text-center text-white">
          <div className="mx-auto h-2 w-40 rounded bg-white/10 animate-pulse" />
          <p className="mt-3 text-white/80">Loading your account…</p>
        </section>
      </main>
    );
  }

  if (!fbUser) {
    return <Navigate to="/splash" replace />;
  }

  return (
    <main className="min-h-[calc(100vh-64px)] bg-gradient-to-b from-[#0b0620] via-[#0a0420] to-[#02010f] px-4 py-10">
      <ScrollPaper
        title="Before you continue"
        subtitle="Confirm your age and acknowledge our terms. You can revisit these later from your profile menu."
      >
        {/* Center the content like you’re “signing the scroll” */}
        <div className="mx-auto w-full max-w-[680px] text-center">
          {/* Age gate */}
          <section className="space-y-4">
            <h2 className="text-lg font-bold text-white">Age</h2>
            <fieldset
              className="grid gap-3 justify-center"
              aria-label="Age confirmation"
            >
              <label className="inline-flex items-center gap-2 justify-center text-white">
                <input
                  type="radio"
                  name="age"
                  value="u13"
                  checked={age === "u13"}
                  onChange={() => setAge("u13")}
                />
                <span>I am under 13 years old</span>
              </label>
              <label className="inline-flex items-center gap-2 justify-center text-white">
                <input
                  type="radio"
                  name="age"
                  value="13-17"
                  checked={age === "13-17"}
                  onChange={() => setAge("13-17")}
                />
                <span>I am 13–17 years old</span>
              </label>
              <label className="inline-flex items-center gap-2 justify-center text-white">
                <input
                  type="radio"
                  name="age"
                  value="18+"
                  checked={age === "18+"}
                  onChange={() => setAge("18+")}
                />
                <span>I am 18 or older</span>
              </label>
            </fieldset>

            {age === "u13" && (
              <div className="mx-auto w-fit rounded-md border border-amber-800/50 bg-amber-200/70 px-3 py-2 text-white text-sm">
                You must be at least 13 to create an account. A parent/guardian
                may contact{" "}
                <a
                  href="mailto:support@spellgather.com"
                  className="underline font-medium"
                >
                  support@spellgather.com
                </a>
                .
              </div>
            )}

            {age === "13-17" && (
              <label className="mt-1 inline-flex items-start gap-2 justify-center text-white">
                <input
                  type="checkbox"
                  checked={acks.guardianConsent}
                  onChange={(e) =>
                    setAcks((s) => ({
                      ...s,
                      guardianConsent: e.target.checked,
                    }))
                  }
                />
                <span>
                  I have read these terms with my parent/guardian, and they
                  consent to my use of Spellgather.
                </span>
              </label>
            )}
          </section>

          {/* Required acknowledgements */}
          <section className="space-y-4 mt-8">
            <h2 className="text-lg font-bold text-white">
              Review &amp; Acknowledge
            </h2>
            <p className="text-white text-sm">
              You can read these now or revisit them anytime via your avatar
              menu.
            </p>

            <ul className="space-y-4 text-left mx-auto w-fit">
              <li className="flex items-start gap-2">
                <input
                  id="ack-terms"
                  type="checkbox"
                  checked={acks.terms}
                  onChange={(e) =>
                    setAcks((s) => ({ ...s, terms: e.target.checked }))
                  }
                />
                <label htmlFor="ack-terms" className="select-none text-white">
                  I agree to the{" "}
                  <Link to="/legal/terms" className="underline">
                    User Agreement &amp; Indemnity
                  </Link>
                  .
                </label>
              </li>

              <li className="flex items-start gap-2">
                <input
                  id="ack-privacy"
                  type="checkbox"
                  checked={acks.privacy}
                  onChange={(e) =>
                    setAcks((s) => ({ ...s, privacy: e.target.checked }))
                  }
                />
                <label
                  htmlFor="ack-privacy"
                  className="select-none text-white"
                >
                  I acknowledge the{" "}
                  <Link to="/legal/privacy" className="underline">
                    Privacy Policy
                  </Link>
                  .
                </label>
              </li>

              <li className="flex items-start gap-2">
                <input
                  id="ack-community"
                  type="checkbox"
                  checked={acks.community}
                  onChange={(e) =>
                    setAcks((s) => ({ ...s, community: e.target.checked }))
                  }
                />
                <label
                  htmlFor="ack-community"
                  className="select-none text-white"
                >
                  I will follow the{" "}
                  <Link to="/legal/community" className="underline">
                    Community Guidelines / Disclaimer
                  </Link>
                  .
                </label>
              </li>

              <li className="flex items-start gap-2">
                <input
                  id="ack-data"
                  type="checkbox"
                  checked={acks.data}
                  onChange={(e) =>
                    setAcks((s) => ({ ...s, data: e.target.checked }))
                  }
                />
                <label htmlFor="ack-data" className="select-none text-white">
                  I have read the{" "}
                  <Link to="/legal/data" className="underline">
                    Data Sharing &amp; Protection Statement
                  </Link>
                  .
                </label>
              </li>
            </ul>
          </section>

          {/* Optional marketing opt-in */}
          <section className="space-y-3 mt-8">
            <h2 className="text-lg font-bold text-white">
              Emails (optional)
            </h2>
            <label className="inline-flex items-start gap-2 justify-center text-white">
              <input
                type="checkbox"
                checked={acks.marketingOptIn}
                onChange={(e) =>
                  setAcks((s) => ({ ...s, marketingOptIn: e.target.checked }))
                }
              />
              <span className="text-white/90">
                Send me occasional product updates and tips. (Change anytime in
                Settings.)
              </span>
            </label>
          </section>

          {/* Signature line & actions */}
          <section className="mt-10">
            {/* Signature flourish */}
            <div className="mx-auto w-[min(360px,80%)] h-10 border-b-4 border-[#5f3b18] relative">
              <span className="absolute -bottom-6 left-1/2 -translate-x-1/2 text-xs text-[#3a210f]">
                Sign here (figuratively ✍️)
              </span>
            </div>

            <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
              <Button
                variant="outline"
                onClick={() => void handleDecline()}
                className="border-[#5f3b18] text-white hover:bg-[#e8cf9f]"
              >
                Decline &amp; Sign out
              </Button>
              <Button
                onClick={() => void handleAccept()}
                disabled={!canAccept || saving}
                className="bg-fuchsia-600 hover:bg-fuchsia-700 text-white"
              >
                {saving ? "Saving…" : "Accept & Continue"}
              </Button>
            </div>

            <div className="mt-6 text-[11px] text-[#3a210f]">
              Legal links:&nbsp;
              <Link to="/legal/terms" className="underline">
                Terms
              </Link>{" "}
              •{" "}
              <Link to="/legal/privacy" className="underline">
                Privacy
              </Link>{" "}
              •{" "}
              <Link to="/legal/community" className="underline">
                Community
              </Link>{" "}
              •{" "}
              <Link to="/legal/copyright" className="underline">
                Copyright
              </Link>
            </div>
          </section>
        </div>
      </ScrollPaper>
    </main>
  );
}

